export interface ContactInactivityTimerCallbacks {
  onThresholdReached?: (threshold: number) => void;
  onRemainingSecondsChange?: (remainingSeconds: number) => void;
  onExpired?: () => void;
}
/**
 * Porcentagens permitidas para disparar o modal de inatividade.
 */
const MODAL_THRESHOLD_PERCENTAGES = [25, 50];

export class ContactInactivityTimer {
  private _intervalId: ReturnType<typeof setInterval> | null = null;
  private readonly thresholds: Record<number, number> = {};
  private _triggeredPercents: number[] = [];
  private _remainingSeconds: number;

  constructor(
    private readonly initialSeconds: number,
    private readonly callbacks: ContactInactivityTimerCallbacks,
  ) {
    if (initialSeconds <= 0) {
      throw new Error('initialSeconds must be greater than 0');
    }

    MODAL_THRESHOLD_PERCENTAGES.forEach((threshold) => {
      this.thresholds[threshold] = Math.floor(
        initialSeconds * (threshold / 100),
      );
    });
    this._remainingSeconds = initialSeconds;

    this.notifyRemainingSecondsChanged();
  }

  public get remainingSeconds(): number {
    return this._remainingSeconds;
  }

  public get running(): boolean {
    return this._intervalId !== null;
  }

  public reset(restart = true): void {
    this._remainingSeconds = this.initialSeconds;
    this._triggeredPercents = [];

    this.notifyRemainingSecondsChanged();
    if (restart) this.start();
  }

  public resetAndPause(): void {
    this.reset(false);
    this.dispose();

    this.notifyRemainingSecondsChanged();
  }

  public dispose(): void {
    if (!this._intervalId) {
      return;
    }

    clearInterval(this._intervalId);
    this._intervalId = null;
  }

  public start(): void {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }

    this._intervalId = setInterval(() => {
      this._remainingSeconds -= 1;
      this.notifyRemainingSecondsChanged();

      MODAL_THRESHOLD_PERCENTAGES.forEach((threshold) => {
        this.notifyThresholdIfNeeded(threshold, this.thresholds[threshold]);
      });

      if (this._remainingSeconds <= 0) {
        this.dispose();
        this.callbacks.onExpired?.();
      }
    }, 1000);
  }

  public get isFinished() {
    return this._remainingSeconds <= 0;
  }

  private notifyRemainingSecondsChanged(): void {
    this.callbacks.onRemainingSecondsChange?.(this._remainingSeconds);
  }

  private notifyThresholdIfNeeded(
    threshold: number,
    thresholdSeconds: number,
  ): void {
    if (
      this.isExpectedThreshold(threshold) &&
      this._triggeredPercents.includes(threshold)
    ) {
      return;
    }

    if (this._remainingSeconds > thresholdSeconds) {
      return;
    }

    if (this.isExpectedThreshold(threshold)) {
      this.includeIfNotExists(threshold);
    }

    this.callbacks.onThresholdReached?.(threshold);
  }

  private includeIfNotExists(thresholdSeconds: number) {
    if (!this._triggeredPercents.includes(thresholdSeconds))
      this._triggeredPercents.push(thresholdSeconds);
  }

  private isExpectedThreshold(threshold: number): boolean {
    return MODAL_THRESHOLD_PERCENTAGES.includes(threshold);
  }
}
